// Editorial design system behaviour (docs/design-bible/editorial-design-bible.md)
// Opt-in: does nothing on pages without a `.ed` wrapper. CSS + IntersectionObserver only.

(function () {
  // main.js loads async, so on a fast (cached) load it can run while the HTML is still parsing: anything below
  // the parse point would never be set up (left hidden by the reveal gate, FAQs left open). Start on DOM ready.
  const start = () => {
    const roots = Array.from(document.querySelectorAll('.ed'));

    if (!roots.length) return;

    const REVEAL_ROOT_MARGIN = '0px 0px -12% 0px';
    const REVEAL_VIEWPORT_FRACTION = 0.88; // mirrors the -12% bottom root margin
    const BREAKPOINT_MD = '(min-width: 768px)';
    const MOTION_OK = '(prefers-reduced-motion: no-preference)';

    const motionAllowed = () => window.matchMedia(MOTION_OK).matches;

    // ------------------------------------------------------------------
    // Scroll restoration. The early gate sets history.scrollRestoration = 'manual' and saves the position on
    // pagehide; here, on a reload or back/forward, it is put back instantly once the page is set up. The
    // browser's own restore runs through the site's smooth scrolling, sweeping the page past the scroll-spy,
    // the header and every reveal on the way.
    // ------------------------------------------------------------------
    function restoreScroll() {
        // A reload or back/forward returns to where the visitor was, even with a #section in the URL
        const nav = performance.getEntriesByType ? performance.getEntriesByType('navigation')[0] : null;
        if (!nav || (nav.type !== 'reload' && nav.type !== 'back_forward')) return;
        let y = 0;
        try {
            y = parseInt(window.sessionStorage.getItem('ed-scroll:' + window.location.pathname), 10) || 0;
        } catch (error) { /* storage blocked: start at the top */ }
        if (y <= 0) return;
        const html = document.documentElement;
        const previous = html.style.scrollBehavior;
        html.style.scrollBehavior = 'auto';
        window.scrollTo(0, y);
        html.style.scrollBehavior = previous;

        // Content above the target settles after DOM ready (font swap, image sizes) and scroll anchoring then
        // nudges the position (about 37px on East Fremantle). Put it back once fonts and images are in, unless
        // the visitor has scrolled in the meantime.
        let touched = false;
        const touch = () => { touched = true; };
        ['wheel', 'touchstart', 'keydown', 'pointerdown', 'mousedown', 'click', 'hashchange'].forEach((type) => window.addEventListener(type, touch, { once: true, passive: true, capture: true }));
        const reapply = () => {
            // Never during a sub-nav jump or its header hold, nor after any scroll the visitor or a script caused since
            if (touched || document.body.dataset.navHold || Math.abs(window.scrollY - y) < 2) return;
            html.style.scrollBehavior = 'auto';
            window.scrollTo(0, y);
            html.style.scrollBehavior = previous;
        };
        if (document.fonts && document.fonts.ready) document.fonts.ready.then(reapply);
        if (document.readyState === 'complete') reapply();
        else window.addEventListener('load', reapply, { once: true });
    }

    // ------------------------------------------------------------------
    // Reveal (M1 to M4): bible 3.8 mechanism
    // ------------------------------------------------------------------
    function initReveal(root) {
        // The inline gate in the page template may already have added ed-motion. If motion
        // isn't possible here, or the 3s failsafe has already shown content, never (re)hide it.
        if (!motionAllowed() || !('IntersectionObserver' in window) || root.hasAttribute('data-ed-failsafe')) {
            root.classList.remove('ed-motion');
            root.classList.add('ed-js');
            return;
        }

        const targets = Array.from(root.querySelectorAll('[data-ed-reveal]'));

        const reveal = (el) => el.setAttribute('data-ed-revealed', 'true');

        try {
            const isInView = (el) => {
                const rect = el.getBoundingClientRect();
                return rect.bottom > 0 && rect.top < window.innerHeight * REVEAL_VIEWPORT_FRACTION;
            };

            // Gate: hidden initial states only apply from here on
            root.classList.add('ed-motion');

            const heroTargets = [];
            const pending = [];

            targets.forEach((el) => {
                if (!isInView(el)) {
                    pending.push(el);
                } else if (el.closest('.ed-hero')) {
                    heroTargets.push(el);
                } else {
                    // Already visible: mark before paint so nothing flashes
                    reveal(el);
                }
            });

            // M9: hero content composes in on load (staggered), starting from the hidden state
            if (heroTargets.length) {
                void root.offsetWidth; // commit the hidden state so the transition runs
                requestAnimationFrame(() => heroTargets.forEach(reveal));
            }

            const waiting = new Set(pending);

            const done = (el) => {
                reveal(el);
                observer.unobserve(el);
                waiting.delete(el);
            };

            const observer = new IntersectionObserver((entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) done(entry.target);
                });
            }, { rootMargin: REVEAL_ROOT_MARGIN, threshold: 0 });

            // Safety sweep: instant jumps (anchor links such as #progress, fast flings, restored scroll)
            // can skip IntersectionObserver callbacks, leaving content above or at the reveal line hidden.
            // Anything that has reached the reveal line is revealed; entering content still animates via IO.
            let sweepQueued = false;
            const sweep = () => {
                sweepQueued = false;
                const line = window.innerHeight * REVEAL_VIEWPORT_FRACTION;
                waiting.forEach((el) => {
                    // Skip elements with no layout box (e.g. collapsed earlier build updates): they reveal when shown
                    if (!el.getClientRects().length) return;
                    if (el.getBoundingClientRect().top < line) done(el);
                });
                // Stop listening only once everything has revealed (collapsed items reveal when shown)
                if (!waiting.size) {
                    window.removeEventListener('scroll', queueSweep);
                    window.removeEventListener('hashchange', queueSweep);
                    window.removeEventListener('load', queueSweep);
                }
            };
            const queueSweep = () => {
                if (sweepQueued) return;
                sweepQueued = true;
                requestAnimationFrame(sweep);
            };

            pending.forEach((el) => observer.observe(el));
            window.addEventListener('scroll', queueSweep, { passive: true });
            window.addEventListener('hashchange', queueSweep);
            window.addEventListener('load', queueSweep); // late layout (fonts, images) can move content past the line
            queueSweep(); // covers a page loaded at an anchor or a restored scroll position
            root.classList.add('ed-js');
        } catch (error) {
            // Never leave content hidden: drop the gate and show everything
            root.classList.remove('ed-motion');
            targets.forEach(reveal);
            root.classList.add('ed-js');
        }
    }

    // ------------------------------------------------------------------
    // Hero video (bible 4.3) + M9
    // ------------------------------------------------------------------
    function initHero(root) {
        const hero = root.querySelector('.ed-hero');
        if (!hero) return;

        const video = hero.querySelector('[data-ed-hero-video]');
        const control = hero.querySelector('[data-ed-hero-control]');

        if (!video) return;

        const sources = {
            desktop: video.getAttribute('data-src-desktop') || '',
            mobile: video.getAttribute('data-src-mobile') || '',
        };

        if (!sources.desktop && !sources.mobile) return;

        const mq = window.matchMedia(BREAKPOINT_MD);
        const connection = navigator.connection || {};
        const constrained = connection.saveData === true || ['2g', 'slow-2g'].includes(connection.effectiveType);

        let userPaused = false;
        let inView = true;
        let active = false; // a source has been assigned

        const label = {
            pause: control ? (control.getAttribute('data-label-pause') || 'Pause background video') : '',
            play: control ? (control.getAttribute('data-label-play') || 'Play background video') : '',
        };

        const setControl = (paused) => {
            if (!control) return;
            control.setAttribute('aria-pressed', paused ? 'true' : 'false');
            control.setAttribute('aria-label', paused ? label.play : label.pause);
        };

        const showControl = () => { if (control) control.hidden = false; };
        const hideControl = () => { if (control) control.hidden = true; };

        const pickSource = () => {
            const preferred = mq.matches ? sources.desktop : sources.mobile;
            return preferred || sources.desktop || sources.mobile;
        };

        const attempt = () => {
            const promise = video.play();
            if (promise && typeof promise.catch === 'function') {
                promise.catch(() => {
                    // Autoplay refused (e.g. low power mode): leave poster, offer Play
                    setControl(true);
                    showControl();
                });
            }
        };

        const assignSource = () => {
            const src = pickSource();
            if (!src) return false;
            if (video.getAttribute('src') !== src) {
                video.removeAttribute('data-ed-playing');
                video.setAttribute('src', src);
                video.load();
            }
            active = true;
            return true;
        };

        video.addEventListener('playing', () => {
            video.setAttribute('data-ed-playing', 'true');
            setControl(false);
            showControl();
        });

        video.addEventListener('error', () => {
            if (!video.hasAttribute('data-ed-playing')) hideControl();
        });

        if (control) {
            control.addEventListener('click', () => {
                if (!active) {
                    // Reduced motion / constrained: the user explicitly asked for video
                    userPaused = false;
                    if (assignSource()) {
                        setControl(false);
                        attempt();
                    }
                    return;
                }

                if (video.paused) {
                    userPaused = false;
                    setControl(false);
                    attempt();
                } else {
                    userPaused = true;
                    video.pause();
                    setControl(true);
                }
            });
        }

        // Pause out of view, resume on return (unless the user paused)
        if ('IntersectionObserver' in window) {
            new IntersectionObserver((entries) => {
                inView = entries[entries.length - 1].isIntersecting;
                if (!active) return;
                if (!inView) {
                    video.pause();
                } else if (!userPaused) {
                    attempt();
                }
            }, { threshold: 0 }).observe(hero);
        }

        // Re-evaluate the source only on a breakpoint crossing
        const onBreakpoint = () => {
            if (!active) return;
            if (assignSource() && !userPaused && inView) attempt();
        };
        if (mq.addEventListener) mq.addEventListener('change', onBreakpoint);
        else if (mq.addListener) mq.addListener(onBreakpoint);

        const start = () => {
            if (!motionAllowed()) {
                // Reduced motion: no autoplay. Poster + static text, Play offered.
                setControl(true);
                showControl();
                return;
            }

            if (constrained) {
                hideControl();
                return;
            }

            if (assignSource()) attempt();
        };

        // Never request video before `load`
        if (document.readyState === 'complete') start();
        else window.addEventListener('load', start, { once: true });
    }

    // ------------------------------------------------------------------
    // Card image carousel (bible 4.8a): native scroll-snap track. JS wires the arrows and dots,
    // tracks the current slide with an IntersectionObserver, and forwards a plain click on the
    // image to the card's stretched link (the media box sits above that link so it can be swiped).
    // ------------------------------------------------------------------
    function initCarousels(root) {
        root.querySelectorAll('[data-ed-carousel]').forEach((carousel) => {
            const track = carousel.querySelector('[data-ed-carousel-track]');
            const prev = carousel.querySelector('[data-ed-carousel-prev]');
            const next = carousel.querySelector('[data-ed-carousel-next]');
            const dots = Array.from(carousel.querySelectorAll('[data-ed-carousel-dot]'));
            const slides = track ? Array.from(track.children) : [];
            if (!track || slides.length < 2 || !prev || !next) return;

            let current = 0;
            let target = 0;

            const behavior = () => (motionAllowed() ? 'smooth' : 'auto');

            const setCurrent = (index) => {
                current = index;
                // Arriving slide settles (CSS); only the current slide carries data-ed-active
                slides.forEach((slide, i) => {
                    if (i === index) slide.setAttribute('data-ed-active', '');
                    else slide.removeAttribute('data-ed-active');
                });
                loadAround(index);
                dots.forEach((dot, i) => {
                    if (i === index) dot.setAttribute('aria-current', 'true');
                    else dot.removeAttribute('aria-current');
                });

                const wasFocused = document.activeElement;
                prev.disabled = index === 0;
                next.disabled = index === slides.length - 1;

                // A disabled button drops focus: hand it to the other arrow so keyboard users keep their place
                if (wasFocused === prev && prev.disabled) next.focus({ preventScroll: true });
                else if (wasFocused === next && next.disabled) prev.focus({ preventScroll: true });
            };

            // While a button-driven scroll is in flight, mid-scroll observer readings must not
            // overwrite the destination (fast double clicks would otherwise lose a step)
            let settling = 0;
            const goTo = (index) => {
                target = Math.max(0, Math.min(slides.length - 1, index));
                window.clearTimeout(settling);
                settling = window.setTimeout(() => { settling = 0; }, 700);
                // Slide width x index: independent of offsetParent, so layout changes can't skew it
                track.scrollTo({ left: target * track.clientWidth, behavior: behavior() });
                if (!('IntersectionObserver' in window)) setCurrent(target);
            };

            if ('IntersectionObserver' in window) {
                // Slides entering or leaving the track trigger a re-read of the scroll position
                const observer = new IntersectionObserver(() => {
                    const index = Math.max(0, Math.min(slides.length - 1, Math.round(track.scrollLeft / (slides[0].offsetWidth || 1))));
                    if (!settling) target = index;
                    if (index !== current) setCurrent(index);
                }, { root: track, threshold: [0.5, 0.6] });
                slides.forEach((slide) => observer.observe(slide));
            }

            // Slides next to the current one (the peeking slide) load as soon as the card is near the viewport,
            // and follow the current slide after that. Only slide 1 loads with the page; lazysizes' own
            // viewport test misses slides clipped inside the horizontal track.
            let near = false;
            const loadSlide = (slide) => {
                const img = slide && slide.querySelector('img.lazyload');
                if (!img) return;
                if (window.lazySizes && window.lazySizes.loader && window.lazySizes.loader.unveil) window.lazySizes.loader.unveil(img);
                else {
                    ['srcset', 'sizes', 'src'].forEach((attr) => {
                        const value = img.getAttribute('data-' + attr);
                        if (value) img.setAttribute(attr, value);
                    });
                }
            };
            const loadAround = (index) => {
                if (near) [index - 1, index, index + 1].forEach((i) => loadSlide(slides[i]));
            };
            if ('IntersectionObserver' in window) {
                const nearObserver = new IntersectionObserver((entries) => {
                    if (!entries.some((entry) => entry.isIntersecting)) return;
                    near = true;
                    nearObserver.disconnect();
                    loadAround(current);
                }, { rootMargin: '300px 0px' });
                nearObserver.observe(carousel);
            } else {
                near = true;
            }

            setCurrent(0);

            // Click feedback: restart the icon "slide-through" animation on every press
            const through = (button) => {
                if (!motionAllowed()) return;
                button.classList.remove('is-through');
                void button.offsetWidth; // restart the animation
                button.classList.add('is-through');
            };
            [prev, next].forEach((button) => button.addEventListener('animationend', () => button.classList.remove('is-through')));

            prev.addEventListener('click', (event) => { event.stopPropagation(); through(prev); goTo(target - 1); });
            next.addEventListener('click', (event) => { event.stopPropagation(); through(next); goTo(target + 1); });
            dots.forEach((dot, i) => dot.addEventListener('click', (event) => { event.stopPropagation(); goTo(i); }));

            // Controls never navigate
            carousel.querySelectorAll('button').forEach((button) => {
                button.addEventListener('pointerdown', (event) => event.stopPropagation());
            });

            // Clicking the image itself still opens the residence (linked cards only)
            const card = carousel.closest('.ed-card');
            const link = card ? card.querySelector('.ed-card__link') : null;
            if (link) {
                track.addEventListener('click', (event) => {
                    if (event.defaultPrevented) return;
                    if (String(window.getSelection() || '')) return; // selecting text is not a click-through
                    link.dispatchEvent(new MouseEvent('click', {
                        bubbles: true,
                        cancelable: true,
                        ctrlKey: event.ctrlKey,
                        metaKey: event.metaKey,
                        shiftKey: event.shiftKey,
                        altKey: event.altKey,
                        button: event.button
                    }));
                });
            }
        });
    }

    // ------------------------------------------------------------------
    // Gallery slider (bible 4.10a): native scroll-snap track. JS wires the arrows, the counter (and its polite
    // live region), arrow keys, and mouse drag on fine pointers. Touch swipes natively. Opening the lightbox is
    // the existing [data-ed-lightbox-open] handler in swiper-config.js (a drag never counts as a click).
    // ------------------------------------------------------------------
    function initGallery(root) {
        root.querySelectorAll('[data-ed-gallery]').forEach((gallery) => {
            const track = gallery.querySelector('[data-ed-gallery-track]');
            const prev = gallery.querySelector('[data-ed-gallery-prev]');
            const next = gallery.querySelector('[data-ed-gallery-next]');
            const counter = gallery.querySelector('[data-ed-gallery-current]');
            const status = gallery.querySelector('[data-ed-gallery-status]');
            const slides = track ? Array.from(track.querySelectorAll('[data-ed-gallery-slide]')) : [];
            if (!track || slides.length < 2 || !prev || !next) return;

            const total = slides.length;
            const pad = (n) => (n < 10 ? '0' + n : String(n));
            let current = 0;
            let announce = 0;

            const behavior = () => (motionAllowed() ? 'smooth' : 'auto');
            const maxScroll = () => track.scrollWidth - track.clientWidth;

            // Nearest slide to the left edge of the track (the snap position)
            const indexFromScroll = () => {
                const x = track.scrollLeft;
                // Only once the track really overflows: before the stylesheet has applied (production CSS loads late)
                // scrollWidth equals clientWidth, and 0 >= -1 would read as the last slide
                if (maxScroll() > 1 && x >= maxScroll() - 1) return total - 1;
                let best = 0;
                let bestDistance = Infinity;
                slides.forEach((slide, i) => {
                    const distance = Math.abs(slide.offsetLeft - x);
                    if (distance < bestDistance) { best = i; bestDistance = distance; }
                });
                return best;
            };

            const setCurrent = (index) => {
                if (index === current) return;
                current = index;
                slides.forEach((slide, i) => {
                    if (i === index) slide.setAttribute('data-ed-active', '');
                    else slide.removeAttribute('data-ed-active');
                });
                if (counter) counter.textContent = pad(index + 1);

                const wasFocused = document.activeElement;
                prev.disabled = index === 0;
                next.disabled = index === total - 1;
                // A disabled button drops focus: hand it to the other arrow so keyboard users keep their place
                if (wasFocused === prev && prev.disabled) next.focus({ preventScroll: true });
                else if (wasFocused === next && next.disabled) prev.focus({ preventScroll: true });

                // Screen readers hear the new position only once the track has settled
                if (status) {
                    window.clearTimeout(announce);
                    announce = window.setTimeout(() => { status.textContent = 'Image ' + (index + 1) + ' of ' + total; }, 250);
                }
            };

            // While a button-driven scroll is in flight, mid-scroll readings must not overwrite the destination
            let target = 0;
            let settling = 0;
            const goTo = (index) => {
                target = Math.max(0, Math.min(total - 1, index));
                window.clearTimeout(settling);
                settling = window.setTimeout(() => { settling = 0; }, 700);
                track.scrollTo({ left: Math.min(slides[target].offsetLeft, maxScroll()), behavior: behavior() });
            };

            const read = () => {
                const index = indexFromScroll();
                if (!settling) target = index;
                setCurrent(index);
            };

            let ticking = false;
            track.addEventListener('scroll', () => {
                if (ticking) return;
                ticking = true;
                window.requestAnimationFrame(() => { ticking = false; read(); });
            }, { passive: true });
            window.addEventListener('resize', read, { passive: true });
            // Layout can change after set-up (late CSS, images, fonts): re-read whenever the track or a slide resizes
            if ('ResizeObserver' in window) {
                const resizeObserver = new ResizeObserver(read);
                resizeObserver.observe(track);
                slides.forEach((slide) => resizeObserver.observe(slide));
            }
            window.addEventListener('load', read, { once: true });

            prev.disabled = true;
            next.disabled = false;

            // Click feedback: restart the icon "slide-through" animation on every press
            const through = (button) => {
                if (!motionAllowed()) return;
                button.classList.remove('is-through');
                void button.offsetWidth; // restart the animation
                button.classList.add('is-through');
            };
            [prev, next].forEach((button) => button.addEventListener('animationend', () => button.classList.remove('is-through')));

            prev.addEventListener('click', () => { through(prev); goTo(target - 1); });
            next.addEventListener('click', () => { through(next); goTo(target + 1); });

            // Arrow keys while focus is anywhere in the gallery (not while typing; never hijack modified keys)
            gallery.addEventListener('keydown', (event) => {
                if (event.metaKey || event.ctrlKey || event.altKey || event.shiftKey) return;
                let to = null;
                if (event.key === 'ArrowLeft') to = target - 1;
                else if (event.key === 'ArrowRight') to = target + 1;
                else if (event.key === 'Home') to = 0;
                else if (event.key === 'End') to = total - 1;
                if (to === null) return;
                event.preventDefault();
                goTo(to);
            });

            // Mouse drag (fine pointers only; touch and pen scroll natively). Snap is paused while dragging and
            // re-applied on release, which settles on the nearest slide in the drag direction.
            const DRAG_THRESHOLD = 6;
            let drag = null;
            let moved = false;
            track.addEventListener('pointerdown', (event) => {
                if (event.pointerType !== 'mouse' || event.button !== 0) return;
                drag = { x: event.clientX, left: track.scrollLeft, id: event.pointerId, index: target };
                moved = false;
            });
            track.addEventListener('pointermove', (event) => {
                if (!drag || event.pointerId !== drag.id) return;
                const dx = event.clientX - drag.x;
                if (!moved && Math.abs(dx) < DRAG_THRESHOLD) return;
                if (!moved) {
                    moved = true;
                    track.classList.add('is-dragging');
                    track.style.scrollSnapType = 'none';
                    track.setPointerCapture(drag.id);
                }
                track.scrollLeft = drag.left - dx;
            });
            const endDrag = (event) => {
                if (!drag || (event && event.pointerId !== drag.id)) return;
                const dx = event.clientX - drag.x;
                const from = drag.index;
                const pointerId = drag.id;
                drag = null;
                if (!moved) return;
                track.classList.remove('is-dragging');
                try { track.releasePointerCapture(pointerId); } catch (error) { /* already released */ }
                // A drag of more than a fifth of a slide moves on one; otherwise settle on the nearest slide
                const step = slides[1].offsetLeft - slides[0].offsetLeft || 1;
                const nearest = indexFromScroll();
                const dest = Math.abs(dx) > step * 0.2 && nearest === from ? from + (dx < 0 ? 1 : -1) : nearest;
                track.style.scrollSnapType = '';
                goTo(dest);
            };
            track.addEventListener('pointerup', endDrag);
            track.addEventListener('pointercancel', endDrag);

            // A drag is not a click: swallow the click that follows it so the lightbox stays shut
            track.addEventListener('click', (event) => {
                if (!moved) return;
                event.preventDefault();
                event.stopPropagation();
                moved = false;
            }, true);
            // Native image/link dragging would fight the drag
            track.addEventListener('dragstart', (event) => event.preventDefault());

            read();
        });
    }

    // ------------------------------------------------------------------
    // Sticky in-page sub-nav (bible 4.5, D7, M8). Read-only on the site header: it only observes
    // body[data-nav-visible] (set by site-header.js) and mirrors it as data-ed-header on the nav.
    // ------------------------------------------------------------------
    function initSubnav(root) {
        const nav = root.querySelector('[data-ed-subnav]');
        if (!nav) return;

        const list = nav.querySelector('[data-ed-subnav-list]');
        const indicator = nav.querySelector('[data-ed-subnav-indicator]');
        const sentinel = root.querySelector('[data-ed-subnav-sentinel]');
        const links = Array.from(nav.querySelectorAll('[data-ed-subnav-link]'));
        const gapProbe = nav.querySelector('[data-ed-probe="gap"]');
        const headerProbe = nav.querySelector('[data-ed-probe="header"]');

        const items = links
            .map((link) => {
                const id = decodeURIComponent((link.getAttribute('href') || '').slice(1));
                return { link, id, section: id ? document.getElementById(id) : null };
            })
            .filter((item) => item.section);

        if (!items.length || !list) return;

        const behavior = () => (motionAllowed() ? 'smooth' : 'auto');

        // ---- Header sync (read-only) ----
        // The bar sticks at the header's actual bottom edge (all widths), read from its live position every frame while
        // the page scrolls or the header slides, so it never passes under the header and moves with it exactly.
        const header = document.getElementById('siteHeader');
        const syncHeader = () => {
            nav.setAttribute('data-ed-header', document.body.getAttribute('data-nav-visible') === 'false' ? 'hidden' : 'shown');
        };
        let trackUntil = 0;
        let tracking = false;
        let lastTop = null;
        const readHeader = () => {
            if (!header) {
                document.documentElement.style.removeProperty('--ed-subnav-top');
                return;
            }
            const bottom = Math.max(0, Math.round(header.getBoundingClientRect().bottom));
            // On the root so sticky columns (--ed-sticky-top) track the same live edge as the bar
            if (bottom === lastTop) return; // unchanged: skip the root style write
            lastTop = bottom;
            document.documentElement.style.setProperty('--ed-subnav-top', bottom + 'px');
        };
        const track = () => {
            readHeader();
            if (performance.now() < trackUntil) requestAnimationFrame(track);
            else tracking = false;
        };
        // Follow the header for the length of its slide (plus a margin) after any change
        const follow = () => {
            trackUntil = performance.now() + 700;
            if (!tracking) {
                tracking = true;
                requestAnimationFrame(track);
            }
        };
        syncHeader();
        readHeader();
        new MutationObserver(() => { syncHeader(); follow(); }).observe(document.body, { attributes: true, attributeFilter: ['data-nav-visible'] });
        window.addEventListener('scroll', follow, { passive: true });
        window.addEventListener('resize', follow, { passive: true });
        if (header) header.addEventListener('transitionrun', follow);

        // ---- Stuck sentinel ----
        if (sentinel && 'IntersectionObserver' in window) {
            new IntersectionObserver((entries) => {
                const entry = entries[entries.length - 1];
                if (entry.isIntersecting || entry.boundingClientRect.top >= 0) nav.removeAttribute('data-ed-stuck');
                else nav.setAttribute('data-ed-stuck', '');
            }, { threshold: 0 }).observe(sentinel);
        }

        // ---- Indicator (M8) ----
        let active = null;

        const placeIndicator = () => {
            if (!active) {
                nav.removeAttribute('data-ed-active');
                return;
            }
            const link = active.link;
            const style = window.getComputedStyle(link);
            const x = link.offsetLeft + (parseFloat(style.paddingLeft) || 0);
            const w = link.offsetWidth - (parseFloat(style.paddingLeft) || 0) - (parseFloat(style.paddingRight) || 0);
            nav.style.setProperty('--ed-sn-x', x + 'px');
            nav.style.setProperty('--ed-sn-w', String(w));
            nav.setAttribute('data-ed-active', '');
        };

        const keepInView = (link) => {
            if (list.scrollWidth <= list.clientWidth + 1) return;
            const target = link.offsetLeft - (list.clientWidth - link.offsetWidth) / 2;
            const max = list.scrollWidth - list.clientWidth;
            list.scrollTo({ left: Math.max(0, Math.min(max, target)), behavior: behavior() });
        };

        const setActive = (item) => {
            if (item === active) return;
            active = item;
            links.forEach((link) => {
                if (active && link === active.link) link.setAttribute('aria-current', 'location');
                else link.removeAttribute('aria-current');
            });
            placeIndicator();
            if (active) keepInView(active.link);
        };

        // ---- Scroll-spy: an observer per target, thin band at the viewport middle ----
        let locked = false;
        let lockTimer = 0;
        const visible = new Set();

        const resolve = () => {
            if (locked) return;
            let next = null;
            items.forEach((item) => { if (visible.has(item.section)) next = item; });
            if (!next) {
                // Between observed sections (or nothing observed yet): last target above the band, else none
                const line = window.innerHeight * 0.45;
                items.forEach((item) => { if (item.section.getBoundingClientRect().top <= line) next = item; });
                const lastEl = items[items.length - 1].section;
                if (next === items[items.length - 1] && lastEl.getBoundingClientRect().bottom < 0) next = null;
            }
            setActive(next);
        };

        if ('IntersectionObserver' in window) {
            const spy = new IntersectionObserver((entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) visible.add(entry.target);
                    else visible.delete(entry.target);
                });
                resolve();
            }, { rootMargin: '-45% 0px -50% 0px', threshold: 0 });
            items.forEach((item) => spy.observe(item.section));
        }
        window.addEventListener('scroll', () => {
            if (!locked) return;
            window.clearTimeout(lockTimer);
            lockTimer = window.setTimeout(() => { locked = false; if (pending) settle(); else resolve(); }, 140);
        }, { passive: true });
        resolve();

        // ---- Click: offset-aware scroll, hash, focus ----
        const probeHeight = (probe) => (probe ? probe.offsetHeight : 0);

        // Where the heading should end up. Past site-header.js's 500px limit the jump holds the header hidden
        // (body[data-nav-hold], released when the visitor takes over), so only the sub-nav needs clearing;
        // above it the header always shows, so clear both.
        const HEADER_HIDE_AFTER = 500;
        const offsetFor = (held) => (held ? 0 : probeHeight(headerProbe)) + nav.offsetHeight + probeHeight(gapProbe);
        const releaseHold = () => { delete document.body.dataset.navHold; };

        const headingFor = (section) => section.querySelector('h1, h2, h3') || section;
        // Land on the whole lockup (eyebrow first), not just the heading
        const targetFor = (section) => section.querySelector('.ed-lockup') || headingFor(section);

        // Layout position, ignoring transforms: content that hasn't revealed yet is still shifted by its rise,
        // and measuring that would leave the landing short and need a visible correction once it reveals
        const layoutTop = (el) => {
            let y = 0;
            for (let node = el; node; node = node.offsetParent) y += node.offsetTop;
            return y;
        };

        // Lockups in the split layouts are sticky (lg+), so their current position depends on the scroll.
        // Aim those at their own sticky line (--ed-sticky-top) using the position they have in the flow, so the
        // column lands exactly where it starts to stick, level with its content column.
        const stickyOf = (heading, section) => {
            for (let el = heading; el && el !== section; el = el.parentElement) {
                if (window.getComputedStyle(el).position === 'sticky') return el;
            }
            return null;
        };
        const flowTop = (sticky) => {
            const parent = sticky.parentElement;
            const parentStyle = window.getComputedStyle(parent);
            return layoutTop(parent)
                + (parseFloat(parentStyle.paddingTop) || 0) + (parseFloat(parentStyle.borderTopWidth) || 0)
                + (parseFloat(window.getComputedStyle(sticky).marginTop) || 0);
        };
        // The sticky line's clearance below the chrome (it tracks the chrome's live edge)
        const stickyClearance = (sticky) => {
            const live = parseFloat(document.documentElement.style.getPropertyValue('--ed-subnav-top'));
            const chrome = (Number.isNaN(live) ? probeHeight(headerProbe) : live) + nav.offsetHeight;
            return (parseFloat(window.getComputedStyle(sticky).top) || 0) - chrome;
        };

        const destination = (target, section, held) => {
            const sticky = stickyOf(target, section);
            if (sticky && sticky.parentElement) {
                const chrome = offsetFor(held) - probeHeight(gapProbe);
                return Math.max(0, flowTop(sticky) - chrome - stickyClearance(sticky));
            }
            return Math.max(0, layoutTop(target) - offsetFor(held));
        };

        // Held only when even the header-hidden landing is past the limit. In the band just above it the
        // header-shown landing would cross the limit and hide the header, so stop on the limit instead.
        // Never aim past the end of the page (the last panel), or the settle loop keeps retrying.
        const landingFor = (target, section) => {
            const held = destination(target, section, true) > HEADER_HIDE_AFTER;
            const top = held ? destination(target, section, true)
                : Math.min(destination(target, section, false), HEADER_HIDE_AFTER);
            const max = document.documentElement.scrollHeight - window.innerHeight;
            return { held, top: Math.max(0, Math.min(top, max)) };
        };

        // Late-loading media can shift the layout during a long scroll: once the scroll settles, nudge the
        // heading back onto its mark (a few tries at most)
        let pending = null;
        const settle = () => {
            if (!pending) return;
            const { top } = landingFor(pending.target, pending.section);
            if (Math.abs(top - window.scrollY) > 3 && pending.tries < 3) {
                pending.tries += 1;
                locked = true;
                window.scrollTo({ top, behavior: 'auto' });
                window.clearTimeout(lockTimer);
                lockTimer = window.setTimeout(() => { locked = false; settle(); }, 160);
                return;
            }
            pending = null;
            resolve();
        };

        // A visitor taking over the scroll cancels the correction and hands the header back to site-header.js
        ['wheel', 'touchstart', 'keydown', 'pointerdown'].forEach((type) => {
            window.addEventListener(type, () => { pending = null; releaseHold(); }, { passive: true });
        });

        items.forEach((item) => {
            item.link.addEventListener('click', (event) => {
                if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
                event.preventDefault();

                const heading = headingFor(item.section);
                const target = targetFor(item.section);
                const { held, top } = landingFor(target, item.section);
                if (held) {
                    document.body.dataset.navHold = 'hidden';
                    window.dispatchEvent(new Event('scroll')); // apply now, even if the jump is a no-op
                } else {
                    releaseHold();
                }

                pending = { target, section: item.section, tries: 0 };
                locked = true;
                window.clearTimeout(lockTimer);
                lockTimer = window.setTimeout(() => { locked = false; settle(); }, 900);
                setActive(item);

                window.scrollTo({ top, behavior: behavior() });

                if (window.history && window.history.replaceState) {
                    window.history.replaceState(null, '', '#' + item.id);
                }

                if (!heading.hasAttribute('tabindex')) heading.setAttribute('tabindex', '-1');
                heading.focus({ preventScroll: true });
            });
        });

        // Keyboard focus is never left under the sticky chrome (WCAG 2.2 focus not obscured). Clear the worst case
        // (header + bar), since scrolling up brings the header back.
        document.addEventListener('focusin', (event) => {
            const el = event.target;
            if (!(el instanceof Element) || nav.contains(el) || (header && header.contains(el))) return;
            if (locked || !el.matches(':focus-visible')) return; // a sub-nav jump places its own focus
            const rect = el.getBoundingClientRect();
            const clear = offsetFor(false);
            if (rect.bottom > 0 && rect.top < clear) window.scrollBy({ top: rect.top - clear, behavior: 'auto' });
        });

        // ---- Layout changes: re-place the indicator ----
        if ('ResizeObserver' in window) {
            new ResizeObserver(placeIndicator).observe(list);
        } else {
            window.addEventListener('resize', placeIndicator);
        }
        if (document.fonts && document.fonts.ready) document.fonts.ready.then(placeIndicator);

        // Transitions only after the first placement, so nothing slides on load
        requestAnimationFrame(() => requestAnimationFrame(() => nav.setAttribute('data-ed-ready', '')));
    }

    // ------------------------------------------------------------------
    // FAQ accordion (bible 4.11, M7). Server markup renders every answer open; here each item
    // closes and its real <button> toggles aria-expanded + data-open. Items are independent.
    // A #faq-{slug} deep link opens and scrolls to its item.
    // ------------------------------------------------------------------
    function initFaq(root) {
        const items = Array.from(root.querySelectorAll('[data-ed-faq]'));
        if (!items.length) return;

        const setOpen = (item, open) => {
            const button = item.querySelector('button[aria-controls]');
            if (!button) return;
            button.setAttribute('aria-expanded', open ? 'true' : 'false');
            item.setAttribute('data-open', open ? 'true' : 'false');
        };

        items.forEach((item) => {
            setOpen(item, false);
            const button = item.querySelector('button[aria-controls]');
            if (button) button.addEventListener('click', () => setOpen(item, item.getAttribute('data-open') !== 'true'));
        });

        const itemFromHash = () => {
            let id = window.location.hash.slice(1);
            try { id = decodeURIComponent(id); } catch (error) { /* keep raw */ }
            return id ? items.find((el) => el.id === id) : null;
        };

        const openFromHash = () => {
            const item = itemFromHash();
            if (!item) return;
            setOpen(item, true);
            item.scrollIntoView({ block: 'start', behavior: 'auto' });
        };

        window.addEventListener('hashchange', openFromHash);
        openFromHash();
        // Late images shift the layout after the first jump: re-settle the scroll once everything has
        // loaded, without re-opening (the visitor may have closed it meanwhile)
        if (document.readyState !== 'complete') {
            window.addEventListener('load', () => {
                const item = itemFromHash();
                if (item) item.scrollIntoView({ block: 'start', behavior: 'auto' });
            }, { once: true });
        }
    }

    // ------------------------------------------------------------------
    // Build updates "Show earlier updates" (bible 4.10)
    // Without JS every update is visible and the toggle stays hidden. Here we collapse the
    // earlier list and reveal the toggle; one click reveals the list (M1) and moves focus
    // to the first revealed title.
    // ------------------------------------------------------------------
    function initTimeline(root) {
        root.querySelectorAll('[data-ed-updates]').forEach((block) => {
            const more = block.querySelector('[data-ed-updates-more]');
            const wrap = block.querySelector('[data-ed-updates-toggle]');
            const button = wrap ? wrap.querySelector('button[aria-controls]') : null;

            if (!more || !wrap || !button) return;

            more.hidden = true;
            wrap.hidden = false;
            button.setAttribute('aria-expanded', 'false');

            button.addEventListener('click', () => {
                more.hidden = false;
                button.setAttribute('aria-expanded', 'true');

                const targets = Array.from(more.querySelectorAll('[data-ed-reveal]'));
                const reveal = () => targets.forEach((el) => el.setAttribute('data-ed-revealed', 'true'));

                if (root.classList.contains('ed-motion')) {
                    // Commit the hidden state first so the items rise in instead of popping
                    void more.offsetWidth;
                    requestAnimationFrame(reveal);
                } else {
                    reveal();
                }

                // The toggle has done its job: hide it, and hand focus to the first new item
                wrap.hidden = true;

                const heading = more.querySelector('.ed-update__title') || more.querySelector('.ed-update');
                if (heading) {
                    heading.setAttribute('tabindex', '-1');
                    heading.focus();
                }
            });
        });
    }


    // ------------------------------------------------------------------
    // Consultation form (bible 4.12, M13). Freeform stays in charge of submitting; this adds inline field
    // errors (aria-invalid + aria-describedby, focus to the first invalid field, no fading), a summary for
    // form-level errors only, client-side required checks (the form is novalidate), and the success swap.
    // Freeform's own banners / error bars are suppressed (the legacy styles are not used here).
    // ------------------------------------------------------------------
    // ------------------------------------------------------------------
    // Section dividers (M2): same-surface hairlines draw in once as they enter view.
    // CSS keeps them hidden only under .ed-motion until data-ed-divider-drawn is set.
    // ------------------------------------------------------------------
    function initDividers(root) {
        const selector = ['canvas', 'stone', 'sage']
            .map((s) => '.ed-surface--' + s + ' + .ed-surface--' + s + ' > .container-wrapper > .padding-wrapper')
            .join(',');
        const dividers = Array.from(root.querySelectorAll(selector));
        if (!dividers.length) return;
        const draw = (el) => el.setAttribute('data-ed-divider-drawn', '');
        if (!root.classList.contains('ed-motion') || !('IntersectionObserver' in window)) {
            dividers.forEach(draw);
            return;
        }
        const waiting = new Set(dividers);
        const observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (!entry.isIntersecting) return;
                draw(entry.target);
                observer.unobserve(entry.target);
                waiting.delete(entry.target);
            });
        }, { rootMargin: REVEAL_ROOT_MARGIN, threshold: 0 });
        dividers.forEach((el) => observer.observe(el));
        // Same safety sweep as reveals: anything already at/above the reveal line draws (anchor jumps)
        const sweep = () => {
            const line = window.innerHeight * REVEAL_VIEWPORT_FRACTION;
            waiting.forEach((el) => {
                if (el.getBoundingClientRect().top < line) {
                    draw(el);
                    observer.unobserve(el);
                    waiting.delete(el);
                }
            });
            if (!waiting.size) window.removeEventListener('scroll', onScroll);
        };
        let queued = false;
        const onScroll = () => {
            if (queued) return;
            queued = true;
            requestAnimationFrame(() => { queued = false; sweep(); });
        };
        window.addEventListener('scroll', onScroll, { passive: true });
        requestAnimationFrame(sweep);
    }

    function initForm(root) {
        // The form wrapper can itself be an .ed scope (e.g. the site-wide Enquire modal renders the page's
        // form with its own .ed scope), so include the root as well as its descendants.
        const wraps = Array.from(root.querySelectorAll('[data-ed-form]'));
        if (root.matches('[data-ed-form]')) wraps.unshift(root);
        wraps.forEach((wrap) => {
            if (wrap.hasAttribute('data-ed-form-ready')) return;
            wrap.setAttribute('data-ed-form-ready', '');
            const form = wrap.querySelector('form');
            const summary = wrap.querySelector('[data-ed-form-summary]');
            const body = wrap.querySelector('[data-ed-form-body]');
            const success = wrap.querySelector('[data-ed-form-success]');
            if (!form || !summary) return;

            const submitButton = () => form.querySelector('[data-freeform-action="submit"]');
            let summaryItems = [];

            const fieldInput = (container) => container && container.querySelector('input:not([type="hidden"]), select, textarea');
            const labelText = (container, handle) => {
                const label = container && container.querySelector('label');
                const text = label ? (label.firstChild && label.firstChild.nodeType === 3 ? label.firstChild.textContent : label.textContent) : '';
                return (text || handle).replace(/\s+/g, ' ').trim();
            };

            const clearErrors = () => {
                form.querySelectorAll('.ed-field__errors').forEach((el) => el.remove());
                form.querySelectorAll('.ed-field--error').forEach((el) => el.classList.remove('ed-field--error'));
                form.querySelectorAll('[aria-invalid]').forEach((el) => {
                    el.removeAttribute('aria-invalid');
                    el.removeAttribute('aria-describedby');
                });
                summaryItems = [];
                summary.hidden = true;
                summary.textContent = '';
            };

            const addFieldError = (handle, messages) => {
                const container = form.querySelector('[data-field-container="' + handle + '"]');
                if (!container) return;
                const input = fieldInput(container);
                const list = document.createElement('ul');
                // Derive from the input's own id (prefixed per instance), so panel + modal never clash
                const errorId = (input && input.id ? input.id : form.id + '-' + handle) + '-errors';
                list.className = 'ed-field__errors ed-small';
                list.id = errorId;
                messages.forEach((message) => {
                    const item = document.createElement('li');
                    const hidden = document.createElement('span');
                    hidden.className = 'ed-sr-only';
                    hidden.textContent = 'Error: ';
                    item.appendChild(hidden);
                    item.appendChild(document.createTextNode(message));
                    list.appendChild(item);
                });
                container.appendChild(list);
                container.classList.add('ed-field--error');
                if (input) {
                    input.setAttribute('aria-invalid', 'true');
                    input.setAttribute('aria-describedby', errorId);
                    summaryItems.push({ id: input.id });
                }
            };

            // Field errors are shown inline only (approved 13 Oct 2026); focus moves to the first invalid field
            const focusFirstError = () => {
                const first = summaryItems.length ? document.getElementById(summaryItems[0].id) : null;
                if (first) first.focus();
            };

            // Summary is only for form-level errors that belong to no field (e.g. spam/captcha rejections)
            const showSummary = (extra) => {
                const items = (extra || []).map((text) => ({ id: '', text }));
                if (!items.length) return;
                summary.textContent = '';
                const title = document.createElement('p');
                title.className = 'ed-form__summary-title';
                title.textContent = 'Please check the following:';
                const list = document.createElement('ul');
                list.className = 'ed-form__summary-list';
                list.setAttribute('role', 'list');
                items.forEach((entry) => {
                    const item = document.createElement('li');
                    if (entry.id) {
                        const link = document.createElement('a');
                        link.className = 'ed-form__summary-link';
                        link.href = '#' + entry.id;
                        link.textContent = entry.text;
                        link.addEventListener('click', (event) => {
                            event.preventDefault();
                            const target = document.getElementById(entry.id);
                            if (target) target.focus();
                        });
                        item.appendChild(link);
                    } else {
                        item.textContent = entry.text;
                    }
                    list.appendChild(item);
                });
                summary.appendChild(title);
                summary.appendChild(list);
                summary.hidden = false;
                summary.focus();
            };

            // Submit label: Freeform renders (and restores) the label as a bare text node, so wrap it in the shared
            // .ed-btn__label to get the optical offset (bible 4.1). Re-wraps after "Sending..." swaps the text back.
            const wrapLabel = () => {
                const button = submitButton();
                if (!button || button.querySelector('.ed-btn__label')) return;
                const text = button.textContent.trim();
                if (!text) return;
                const label = document.createElement('span');
                label.className = 'ed-btn__label';
                label.textContent = text;
                button.textContent = '';
                button.appendChild(label);
            };
            wrapLabel();
            const labelObserver = new MutationObserver(wrapLabel);
            const watchButton = () => {
                const button = submitButton();
                if (button) labelObserver.observe(button, { childList: true });
            };
            watchButton();

            form.addEventListener('freeform-ready', watchButton);

            // Freeform's auto-scroll would fight the focus move to the first invalid field
            const disableAutoScroll = () => { if (form.freeform && form.freeform.setOption) form.freeform.setOption('autoScroll', false); };
            form.addEventListener('freeform-ready', disableAutoScroll);
            disableAutoScroll();

            // Client-side checks before Freeform submits: required fields and email format
            form.addEventListener('freeform-on-submit', (event) => {
                clearErrors();
                form.querySelectorAll('[aria-required="true"]').forEach((input) => {
                    const container = input.closest('[data-field-container]');
                    if (!container) return;
                    const handle = container.getAttribute('data-field-container');
                    const value = (input.type === 'checkbox' ? (input.checked ? 'x' : '') : input.value).trim();
                    if (!value) {
                        // The consent label is the whole privacy sentence, so it gets its own short message
                        addFieldError(handle, [input.type === 'checkbox' ? 'Please agree to continue.' : labelText(container, handle) + ' is required.']);
                    } else if (input.type === 'email' && input.validity && input.validity.typeMismatch) {
                        addFieldError(handle, ['Please enter a valid email address.']);
                    }
                });
                if (summaryItems.length) {
                    event.preventDefault();
                    focusFirstError();
                }
            });

            form.addEventListener('freeform-ajax-before-submit', () => {
                clearErrors();
                const button = submitButton();
                if (button) button.setAttribute('aria-busy', 'true');
            });

            const clearBusy = () => {
                const button = submitButton();
                if (button) button.removeAttribute('aria-busy');
            };
            form.addEventListener('freeform-ajax-after-submit', clearBusy);
            form.addEventListener('freeform-after-failed-submit', clearBusy);

            // Server-side errors: take over rendering (no fading bars, no banner)
            form.addEventListener('freeform-render-field-errors', (event) => {
                event.preventDefault();
                clearErrors();
                const errors = event.errors || {};
                Object.keys(errors).forEach((handle) => addFieldError(handle, [].concat(errors[handle])));
                focusFirstError();
            });

            form.addEventListener('freeform-render-form-errors', (event) => {
                event.preventDefault();
                const extra = Array.isArray(event.errors) ? event.errors.filter(Boolean) : [];
                if (extra.length) showSummary(extra);
            });

            form.addEventListener('freeform-render-success', (event) => event.preventDefault());

            // Success (M13): form fades out, then the success block takes its place; focus to its heading
            form.addEventListener('freeform-ajax-success', () => {
                if (!success || !body) return;
                clearErrors();
                const swap = () => {
                    body.hidden = true;
                    success.hidden = false;
                    const targets = Array.from(success.querySelectorAll('[data-ed-reveal]'));
                    const reveal = () => targets.forEach((el) => el.setAttribute('data-ed-revealed', 'true'));
                    if (root.classList.contains('ed-motion')) {
                        void success.offsetWidth;
                        requestAnimationFrame(reveal);
                    } else {
                        reveal();
                    }
                    const heading = success.querySelector('[data-ed-form-success-heading]');
                    if (heading) heading.focus();
                };
                if (motionAllowed()) {
                    wrap.classList.add('ed-form--done');
                    window.setTimeout(swap, parseFloat(getComputedStyle(body).transitionDuration) * 1000 || 0);
                } else {
                    swap();
                }
            });
        });
    }

    // ------------------------------------------------------------------
    // Quote fit (bible 4.10). Two quotes side by side get their font sizes scaled between the long-quote size
    // (min) and the normal quote size (max) so the two blockquotes end up the same height. The result is the
    // --ed-quote-fit custom property; without JS (or when the quotes stack) the CSS sizes apply unchanged.
    // ------------------------------------------------------------------
    const QUOTE_FIT_FLOOR = 0.85; // the longer quote may ease to this share of the long-quote size when that is not enough

    const QUOTE_FIT_RATIO = 1.5; // the two sizes never differ by more than this, so the pair still reads as a set

    function initQuoteFit(root) {
        const rows = Array.from(root.querySelectorAll('.ed-quotes--pairs'));

        rows.forEach((row) => {
            const figs = Array.from(row.querySelectorAll('.ed-testimonial'));
            if (figs.length < 2) return;
            const quotes = figs.map((f) => f.querySelector('.ed-testimonial__quote'));
            if (quotes.some((q) => !q)) return;

            const setSize = (q, px) => q.style.setProperty('--ed-quote-fit', `${px}px`);
            const heightOf = (q) => q.getBoundingClientRect().height;
            const sizeOf = (q) => parseFloat(getComputedStyle(q).fontSize);

            // Fit each side-by-side pair (rows of two); a pair is "laid out" when the figures share a top
            const fitPair = (a, b) => {
                a.quote.style.removeProperty('--ed-quote-fit');
                b.quote.style.removeProperty('--ed-quote-fit');
                a.nat = sizeOf(a.quote);
                b.nat = sizeOf(b.quote);
                if (a.figure.offsetTop !== b.figure.offsetTop) return; // stacked: CSS sizes apply

                // Range from the live CSS: min = the long-quote size, max = the normal quote size
                const probes = document.createElement('div');
                probes.setAttribute('aria-hidden', 'true');
                probes.style.cssText = 'position:absolute;visibility:hidden;pointer-events:none;';
                probes.innerHTML = '<figure class="ed-testimonial"><blockquote class="ed-quote ed-testimonial__quote"></blockquote></figure>'
                    + '<figure class="ed-testimonial ed-testimonial--long"><blockquote class="ed-quote ed-testimonial__quote"></blockquote></figure>';
                row.appendChild(probes);
                const max = sizeOf(probes.children[0].firstChild);
                const min = Math.min(sizeOf(probes.children[1].firstChild), max);
                probes.remove();
                if (!(max > min)) return;

                // Wrapping makes height step with size (a line more or fewer), so rather than a plain binary search each
                // quote's height is sampled across the range (0.5px steps) and the best pair is picked. A pair within
                // a quarter of a line is a match; of those, the one that moves the sizes least from their CSS defaults
                // wins (the shorter text grows towards max, the longer eases down only if it has to). If nothing
                // matches, the closest pair wins.
                const floor = min * QUOTE_FIT_FLOOR;
                const natA = parseFloat(a.nat);
                const natB = parseFloat(b.nat);
                const sizes = [];
                for (let s = floor; s < max; s += 0.5) sizes.push(s);
                sizes.push(max);
                const sample = (q) => sizes.map((s) => { setSize(q, s); return heightOf(q); });
                const hA = sample(a.quote);
                const hB = sample(b.quote);
                const tolerance = (parseFloat(getComputedStyle(a.quote).lineHeight) / parseFloat(getComputedStyle(a.quote).fontSize)) * min / 4;
                let best = null;
                for (let i = 0; i < sizes.length; i += 1) {
                    for (let j = 0; j < sizes.length; j += 1) {
                        if (Math.max(sizes[i], sizes[j]) / Math.min(sizes[i], sizes[j]) > QUOTE_FIT_RATIO) continue;
                        const diff = Math.abs(hA[i] - hB[j]);
                        const move = Math.abs(sizes[i] - natA) + Math.abs(sizes[j] - natB);
                        const score = diff <= tolerance ? move : 1000 + diff;
                        if (!best || score < best.score) best = { score, i, j };
                    }
                }
                setSize(a.quote, sizes[best.i]);
                setSize(b.quote, sizes[best.j]);
            };

            const fitAll = () => {
                const items = figs.map((figure, i) => ({ figure, quote: quotes[i] }));
                for (let i = 0; i + 1 < items.length; i += 2) fitPair(items[i], items[i + 1]);
            };

            let frame = 0;
            const schedule = () => {
                if (frame) return;
                frame = requestAnimationFrame(() => { frame = 0; fitAll(); });
            };

            fitAll();
            if (document.fonts && document.fonts.ready) document.fonts.ready.then(schedule);
            if (window.ResizeObserver) new ResizeObserver(schedule).observe(row);
        });
    }

    // ------------------------------------------------------------------
    // Quote slider (residence quote spots, bible 4.10): one quote at a time, the slides stacked in one grid cell.
    // Height lock: every slide's quote font size is scaled (within initQuoteFit's clamps: the long-quote size x 0.85
    // up to the normal quote size) so all slides render at the same height; the box is then fixed to that height, so
    // changing slides never moves the page. The caption is pinned to the bottom of the cell in CSS.
    // ------------------------------------------------------------------
    function initQuoteSlider(root) {
        root.querySelectorAll('[data-ed-quotes-slider]').forEach((slider) => {
            const viewport = slider.querySelector('[data-ed-quotes-viewport]');
            const slides = Array.from(slider.querySelectorAll('[data-ed-quotes-slide]'));
            const prev = slider.querySelector('[data-ed-quotes-prev]');
            const next = slider.querySelector('[data-ed-quotes-next]');
            const counter = slider.querySelector('[data-ed-quotes-current]');
            const status = slider.querySelector('[data-ed-quotes-status]');
            if (!viewport || slides.length < 2 || !prev || !next) return;

            const total = slides.length;
            const pad = (n) => (n < 10 ? '0' + n : String(n));
            const quotes = slides.map((slide) => slide.querySelector('.ed-testimonial__quote'));
            if (quotes.some((q) => !q)) return;
            let current = 0;

            // ---- Height lock ----
            const sizeOf = (q) => parseFloat(getComputedStyle(q).fontSize);
            const setSize = (q, px) => q.style.setProperty('--ed-quote-fit', `${px}px`);
            const heightOf = (q) => q.getBoundingClientRect().height;

            const clamps = () => {
                const probes = document.createElement('div');
                probes.setAttribute('aria-hidden', 'true');
                probes.style.cssText = 'position:absolute;visibility:hidden;pointer-events:none;';
                probes.innerHTML = '<figure class="ed-testimonial"><blockquote class="ed-quote ed-testimonial__quote"></blockquote></figure>'
                    + '<figure class="ed-testimonial ed-testimonial--long"><blockquote class="ed-quote ed-testimonial__quote"></blockquote></figure>';
                slider.appendChild(probes);
                const max = sizeOf(probes.children[0].firstChild);
                const min = Math.min(sizeOf(probes.children[1].firstChild), max);
                probes.remove();
                return { min, max };
            };

            let lastWidth = 0;
            const fit = () => {
                lastWidth = viewport.clientWidth;
                viewport.style.minBlockSize = '';
                quotes.forEach((q) => q.style.removeProperty('--ed-quote-fit'));
                if (!lastWidth) return;

                const { min, max } = clamps();
                if (max > min) {
                    const floor = min * QUOTE_FIT_FLOOR;
                    const sizes = [];
                    for (let s = floor; s < max; s += 0.5) sizes.push(s);
                    sizes.push(max);

                    // Per slide: runs of sizes that wrap to the same number of lines. Inside a run the height is
                    // lines x line-height x size, so any height in [n*lh*lo, n*lh*hi] is reachable exactly.
                    const infos = quotes.map((q) => {
                        const nat = (q.style.removeProperty('--ed-quote-fit'), sizeOf(q));
                        const segs = [];
                        sizes.forEach((s) => {
                            setSize(q, s);
                            const lh = parseFloat(getComputedStyle(q).lineHeight);
                            const px = Number.isFinite(lh) ? lh : s * 1.3;
                            const h = heightOf(q);
                            const n = Math.max(1, Math.round(h / px));
                            const ratio = h / (n * s);
                            const last = segs[segs.length - 1];
                            if (last && last.n === n) { last.hi = s; last.ratio = ratio; } else segs.push({ n, lo: s, hi: s, ratio });
                        });
                        q.style.removeProperty('--ed-quote-fit');
                        segs.forEach((g) => { g.hLo = g.n * g.ratio * g.lo; g.hHi = g.n * g.ratio * g.hi; });
                        return { nat, segs };
                    });

                    // Candidate heights: every run end and every run's natural-size height
                    const cands = [];
                    infos.forEach((info) => info.segs.forEach((g) => {
                        cands.push(g.hLo, g.hHi, Math.min(g.hHi, Math.max(g.hLo, g.n * g.ratio * info.nat)));
                    }));
                    const pick = (info, H) => {
                        let best = null;
                        info.segs.forEach((g) => {
                            const target = Math.min(g.hHi, Math.max(g.hLo, H));
                            const dist = Math.abs(target - H);
                            const size = target / (g.n * g.ratio);
                            if (!best || dist < best.dist - 0.01) best = { dist, size };
                            else if (Math.abs(dist - best.dist) <= 0.01 && Math.abs(size - info.nat) < Math.abs(best.size - info.nat)) best = { dist, size };
                        });
                        return best;
                    };
                    let winner = null;
                    cands.forEach((H) => {
                        const picks = infos.map((info) => pick(info, H));
                        const miss = picks.reduce((a, p) => a + (p.dist > 0.4 ? p.dist : 0), 0);
                        const move = picks.reduce((a, p, i) => a + Math.abs(p.size - infos[i].nat), 0);
                        const score = miss * 1000 + move;
                        if (!winner || score < winner.score) winner = { score, picks };
                    });
                    quotes.forEach((q, i) => setSize(q, Math.round(winner.picks[i].size * 100) / 100));
                }

                // Fix the box to the tallest slide (all slides now match, so this is every slide's height)
                const tallest = Math.max(...slides.map((slide) => slide.getBoundingClientRect().height));
                viewport.style.minBlockSize = `${Math.ceil(tallest)}px`;
            };

            let frame = 0;
            const schedule = () => {
                if (frame) return;
                frame = requestAnimationFrame(() => { frame = 0; fit(); });
            };

            // ---- Slides ----
            const through = (button) => {
                if (!motionAllowed()) return;
                button.classList.remove('is-through');
                void button.offsetWidth; // restart the animation
                button.classList.add('is-through');
            };
            [prev, next].forEach((button) => button.addEventListener('animationend', () => button.classList.remove('is-through')));

            const show = (index, announce) => {
                current = index;
                slides.forEach((slide, i) => {
                    slide.toggleAttribute('data-ed-active', i === index);
                    slide.toggleAttribute('data-ed-past', i < index);
                    if (i === index) slide.removeAttribute('inert'); else slide.setAttribute('inert', '');
                });
                if (counter) counter.textContent = pad(index + 1);
                const wasFocused = document.activeElement;
                prev.disabled = index === 0;
                next.disabled = index === total - 1;
                // A disabled button drops focus: hand it to the other arrow so keyboard users keep their place
                if (wasFocused === prev && prev.disabled) next.focus({ preventScroll: true });
                else if (wasFocused === next && next.disabled) prev.focus({ preventScroll: true });
                if (announce && status) status.textContent = 'Quote ' + (index + 1) + ' of ' + total;
            };
            const goTo = (index) => {
                const to = Math.max(0, Math.min(total - 1, index));
                if (to !== current) show(to, true);
            };

            prev.addEventListener('click', () => { through(prev); goTo(current - 1); });
            next.addEventListener('click', () => { through(next); goTo(current + 1); });

            // Arrow keys while focus is anywhere in the slider (never hijack modified keys)
            slider.addEventListener('keydown', (event) => {
                if (event.metaKey || event.ctrlKey || event.altKey || event.shiftKey) return;
                let to = null;
                if (event.key === 'ArrowLeft') to = current - 1;
                else if (event.key === 'ArrowRight') to = current + 1;
                else if (event.key === 'Home') to = 0;
                else if (event.key === 'End') to = total - 1;
                if (to === null) return;
                event.preventDefault();
                goTo(to);
            });

            // Swipe (touch and pen; the mouse has the arrows). A mostly horizontal move of 40px or more changes slide.
            let swipe = null;
            viewport.addEventListener('pointerdown', (event) => {
                if (event.pointerType === 'mouse') return;
                swipe = { x: event.clientX, y: event.clientY, id: event.pointerId };
            });
            const endSwipe = (event) => {
                if (!swipe || event.pointerId !== swipe.id) return;
                const dx = event.clientX - swipe.x;
                const dy = event.clientY - swipe.y;
                swipe = null;
                if (event.type === 'pointercancel') return;
                if (Math.abs(dx) >= 40 && Math.abs(dx) > Math.abs(dy) * 1.5) goTo(current + (dx < 0 ? 1 : -1));
            };
            viewport.addEventListener('pointerup', endSwipe);
            viewport.addEventListener('pointercancel', endSwipe);

            show(0, false);
            fit();
            viewport.setAttribute('data-ed-ready', '');
            if (document.fonts && document.fonts.ready) document.fonts.ready.then(schedule);
            window.addEventListener('load', schedule, { once: true });
            // Refit when the width changes (resize, breakpoints); the fit itself changes only the height
            if (window.ResizeObserver) {
                new ResizeObserver(() => { if (viewport.clientWidth !== lastWidth) schedule(); }).observe(viewport);
            }
        });
    }

    // ------------------------------------------------------------------
    // Video facade (residence page, bible 4.10). The poster + play control stand in for the player, so no third-party
    // code loads until the visitor presses play. Click: the player replaces the poster (YouTube nocookie, Vimeo or a
    // native <video>) and focus moves to it. Without JS the control is a link to the watch URL.
    // ------------------------------------------------------------------
    function initVideoFacade(root) {
        root.querySelectorAll('[data-ed-video]').forEach((box) => {
            const play = box.querySelector('[data-ed-video-play]');
            if (!play) return;

            const provider = box.dataset.provider;
            const media = box.dataset.media;
            const title = box.dataset.title || 'video';

            // YouTube poster: maxresdefault does not exist for every video. YouTube answers with a 404 or a
            // 120px-wide placeholder, so fall back to hqdefault in either case.
            const yt = box.querySelector('img[data-ed-video-yt]');
            if (yt) {
                const toHq = () => {
                    if (yt.dataset.fallback === '2') return;
                    if (yt.dataset.fallback === '1') {
                        // hqdefault failed too: last resort is the residence hero image
                        yt.dataset.fallback = '2';
                        if (box.dataset.hero) yt.src = box.dataset.hero;
                        return;
                    }
                    yt.dataset.fallback = '1';
                    yt.src = 'https://i.ytimg.com/vi/' + yt.dataset.edVideoYt + '/hqdefault.jpg';
                };
                const check = () => {
                    if (!yt.complete) return;
                    if (yt.dataset.fallback !== '2' && (!yt.naturalWidth || yt.naturalWidth <= 120)) toHq();
                };
                yt.addEventListener('error', toHq);
                yt.addEventListener('load', check);
                check();
            }

            play.addEventListener('click', (event) => {
                event.preventDefault();
                let player;

                if (provider === 'youtube') {
                    player = document.createElement('iframe');
                    player.src = 'https://www.youtube-nocookie.com/embed/' + encodeURIComponent(media) + '?autoplay=1&rel=0&playsinline=1';
                    player.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
                } else if (provider === 'vimeo') {
                    player = document.createElement('iframe');
                    player.src = 'https://player.vimeo.com/video/' + encodeURIComponent(media) + '?autoplay=1&title=0&byline=0&dnt=1';
                    player.allow = 'autoplay; fullscreen; picture-in-picture';
                } else {
                    player = document.createElement('video');
                    player.controls = true;
                    player.playsInline = true;
                    player.src = media;
                    if (box.dataset.poster) player.poster = box.dataset.poster;
                }

                player.className = 'ed-video__frame';
                if (player.tagName === 'IFRAME') {
                    player.title = 'Video: ' + title;
                    player.allowFullscreen = true;
                    player.setAttribute('referrerpolicy', 'strict-origin-when-cross-origin');
                } else {
                    player.setAttribute('aria-label', 'Video: ' + title);
                }

                box.replaceChildren(player);
                box.removeAttribute('data-ed-video');
                player.setAttribute('tabindex', '-1');
                player.focus({ preventScroll: true });
                if (player.tagName === 'VIDEO') {
                    const started = player.play();
                    if (started && started.catch) started.catch(() => {});
                }
            });
        });
    }


    // ------------------------------------------------------------------
    // Process line (bible 4.7, M11, 26 Oct 2026, pending approval): measures the button against the rail so the line
    // can run down and across into it, and pulses each dot and then the button as the line's leading edge reaches
    // them. The edge is read from the real scroll-driven animations (getComputedTiming().progress), so the pulses
    // cannot drift from the drawing. Where scroll-driven animation is unsupported, a dot pulses as it crosses 70% of
    // the viewport. Each target pulses once per arrival and re-arms only when the line has retreated past it.
    // ------------------------------------------------------------------
    function initProcess(root) {
        root.querySelectorAll('.ed-steps').forEach((steps) => {
            const cta = steps.closest('.ed-process__main')?.querySelector('.ed-process__cta .ed-btn');
            const link = steps.querySelector('.ed-steps__link');
            const rail = steps.querySelector('.ed-steps__progress');
            const markers = Array.from(steps.querySelectorAll('.ed-step__marker'));
            if (!markers.length) return;

            const pageTop = (el) => { let y = 0; for (let n = el; n; n = n.offsetParent) y += n.offsetTop; return y; };
            const pageLeft = (el) => { let x = 0; for (let n = el; n; n = n.offsetParent) x += n.offsetLeft; return x; };
            const progressOf = (el) => {
                const a = el && el.getAnimations ? el.getAnimations()[0] : null;
                if (!a || !a.effect) return null;
                const p = a.effect.getComputedTiming().progress;
                return typeof p === 'number' ? p : 0;
            };

            let geo = null;
            const measure = () => {
                const h = steps.offsetHeight;
                const top = pageTop(steps);
                const markerY = pageTop(markers[0]) + markers[0].offsetHeight / 2 - top;
                const railLen = h - markerY;
                const dots = markers.map((m) => pageTop(m) + m.offsetHeight / 2 - top - markerY);
                geo = { railLen, dots, top, h };
                if (!link || !cta) return;
                const runY = pageTop(cta) + cta.offsetHeight / 2 - (top + h);
                const runX = Math.max(0, pageLeft(cta) - pageLeft(steps));
                const hasRun = runX > 1;
                // Scroll distance per px of line at the rail's pace (range: entry 20% to cover 60% of the list)
                const vh = window.innerHeight;
                const k = railLen / Math.max(1, 0.6 * (vh + h) - 0.2 * h);
                steps.style.setProperty('--ed-run-y', runY + 'px');
                steps.style.setProperty('--ed-run-x', (hasRun ? runX : 0) + 'px');
                const pathLen = runY + (hasRun ? runX : 0);
                steps.style.setProperty('--ed-run-f', String(hasRun ? runY / pathLen : 1));
                steps.style.setProperty('--ed-run-total', Math.max(1, pathLen / k) + 'px');
                steps.toggleAttribute('data-ed-run', runY > 0);
                steps.toggleAttribute('data-ed-run-across', hasRun);
                geo.runY = runY;
                geo.hasRun = hasRun;
            };

            const blip = (el, attr, onEnd) => {
                el.removeAttribute(attr);
                void el.offsetWidth;
                el.setAttribute(attr, '');
                el.dataset.edPulses = String((parseInt(el.dataset.edPulses, 10) || 0) + 1);
                el.addEventListener('animationend', function done(e) {
                    if (e.target !== el) return;
                    el.removeEventListener('animationend', done);
                    el.removeAttribute(attr);
                }, { once: false });
                if (onEnd) onEnd();
            };

            const inView = (el) => {
                const r = el.getBoundingClientRect();
                return r.bottom > 0 && r.top < window.innerHeight * 0.95;
            };

            // targets: one per dot, then the button. reached(): line past it; el: what to pulse.
            const state = markers.map(() => ({ fired: false }));
            const ctaState = { fired: false };
            let primed = false;
            let ticking = false;

            const tick = () => {
                ticking = false;
                if (!geo) return;
                const animated = rail && getComputedStyle(rail).display !== 'none' && progressOf(rail) !== null;
                const motion = motionAllowed();
                const edgeLen = animated ? progressOf(rail) * geo.railLen : null;
                const vy = window.innerHeight * 0.7;
                const check = (st, reached, retreated, el, attr, prep, lit) => {
                    if (lit && reached) el.setAttribute('data-ed-lit', '');
                    if (!reached) { if (retreated) { st.fired = false; if (lit) el.removeAttribute('data-ed-lit'); } return; }
                    if (st.fired) return;
                    if (!primed) { st.fired = true; return; }
                    if (!motion || !inView(el)) return;
                    st.fired = true;
                    if (prep) prep();
                    blip(el, attr);
                };
                markers.forEach((m, i) => {
                    const reached = animated ? edgeLen > geo.dots[i] + 0.5 : m.getBoundingClientRect().top < vy;
                    const retreated = animated ? edgeLen < geo.dots[i] - 20 : m.getBoundingClientRect().top > vy + 20;
                    check(state[i], reached, retreated, m, 'data-ed-blip', null, true);
                });
                if (cta && link && steps.hasAttribute('data-ed-run')) {
                    const p = animated ? progressOf(link) : null;
                    const reached = p !== null ? p >= 0.995 : cta.getBoundingClientRect().top < vy;
                    const retreated = p !== null ? p < 0.9 : cta.getBoundingClientRect().top > vy + 20;
                    check(ctaState, reached, retreated, cta, 'data-ed-pulse', () => {
                        cta.style.setProperty('--ed-pulse-sx', ((cta.offsetWidth + 20) / cta.offsetWidth).toFixed(3));
                        cta.style.setProperty('--ed-pulse-sy', ((cta.offsetHeight + 20) / cta.offsetHeight).toFixed(3));
                    });
                }
            };

            const request = () => { if (!ticking) { ticking = true; requestAnimationFrame(tick); } };

            measure();
            window.addEventListener('scroll', request, { passive: true });
            window.addEventListener('resize', () => { measure(); request(); });
            if (window.ResizeObserver) {
                new ResizeObserver(() => { measure(); request(); }).observe(steps);
            }
            if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => { measure(); request(); });
            // Prime after the restored scroll position is applied: dots already passed never pulse on load
            requestAnimationFrame(() => requestAnimationFrame(() => {
                tick();
                primed = true;
                // Muted start state only now that JS has set the dots' lit state (never without JS / under reduced motion)
                if (motionAllowed()) {
                    steps.setAttribute('data-ed-dots', '');
                    requestAnimationFrame(() => requestAnimationFrame(() => steps.setAttribute('data-ed-dots', 'live')));
                }
                request();
            }));
        });
    }

    // ------------------------------------------------------------------
    // Image loading (bible 3.5): stone ground, then the LQIP, then the full image fades in once it has loaded
    // and decoded. CSS hides every `.ed-media__img` (and the hero poster) until it carries `.is-loaded`. This is
    // independent of lazysizes' classes so the carousel unveil, eager images and cached images all behave alike.
    // ------------------------------------------------------------------
    function initImages(root) {
        const isReal = (img) => img.complete && img.naturalWidth > 0 && !String(img.currentSrc || img.getAttribute('src') || '').startsWith('data:');
        root.querySelectorAll('.ed-media__img, .ed-hero__poster').forEach((img) => {
            if (img.tagName !== 'IMG' || img.classList.contains('is-loaded')) return;
            let settled = false;
            const show = () => {
                if (settled) return;
                settled = true;
                img.classList.add('is-loaded');
            };
            const ready = () => {
                if (settled) return;
                if (!img.decode) { show(); return; }
                img.decode().then(show, show); // a failed decode still reveals (never leave an image hidden)
            };
            const check = () => { if (!settled && isReal(img)) ready(); };
            ['load', 'lazyloaded'].forEach((type) => img.addEventListener(type, check));
            img.addEventListener('error', () => { if (!String(img.getAttribute('src') || '').startsWith('data:')) show(); });
            // Already complete before the listeners attached (cached, or loaded during parse)
            if (isReal(img)) show(); // no decode wait: it is already on screen
        });
        root.classList.add('ed-img-js'); // after the loop, in the same task: images already on screen never flash
    }

    roots.forEach((root) => {
        initImages(root);
        initReveal(root);
        initHero(root);
        initSubnav(root);
        initFaq(root);
        initTimeline(root);
        initCarousels(root);
        initGallery(root);
        initVideoFacade(root);
        initForm(root);
        initDividers(root);
        initQuoteFit(root);
        initQuoteSlider(root);
        initProcess(root);
    });

    // After set-up, so layout changes it makes (e.g. collapsed build updates) are already in place. The reveal sweep
    // picks up whatever is in view on the next frame.
    restoreScroll();
  };

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', start, { once: true });
    } else {
        start();
    }
})();
